import { PricedProduct } from "@medusajs/medusa/dist/types/pricing"

import { SearchProduct } from "@lib/clients/nelo-api-client"
import { sendGoogleAnalyticsEvent, sendPersonalizeAnalyticsEvent } from "@modules/common/utils/analytics"
import { getPriceAndDiscounts } from "@modules/common/utils/productPreview"

/**
 * Track a product list view event for analytics
 * This sends a standardized VIEW_ITEM_LIST event to Google Analytics
 *
 * @param product Individual product to track
 * @param index Position of the product in the list (0-based)
 * @param listId Unique identifier for the list
 * @param listName Optional name of the list
 * @param listSource Optional source of the list
 * @param listLocation Optional location/path of the list
 * @param listIndex Optional index of the list section
 * @returns boolean indicating if event was sent
 */
export const trackProductListView = (
  product: SearchProduct,
  index: number,
  listId: string,
  listName?: string,
  listSource?: string,
  listLocation?: string,
  listIndex?: number
) => {
  const item = mapSearchProductToAnalyticsItem(product, index)

  // Send the VIEW_ITEM_LIST event
  sendGoogleAnalyticsEvent("VIEW_ITEM_LIST", {
    item_list_id: listId,
    item_list_name: listName,
    item_list_source: listSource,
    item_list_location: listLocation,
    // refers to the index of the list, like the index of a section in the home screen, not the index of the element
    item_list_index: listIndex,
    items: [item],
  })

  appendImpressions([product])
}

export const trackProductSelect = (
  product: SearchProduct,
  index: number,
  listId: string,
  listName?: string,
  listSource?: string,
  listLocation?: string,
  listIndex?: number
) => {
  const item = mapSearchProductToAnalyticsItem(product, index)

  sendGoogleAnalyticsEvent("SELECT_ITEM", {
    item_list_id: listId,
    item_list_name: listName,
    item_list_source: listSource,
    item_list_location: listLocation,
    // refers to the index of the list, like the index of a section in the home screen, not the index of the element
    item_list_index: listIndex,
    items: [item],
  })
}

export const trackProductView = (product: PricedProduct) => {
  const items = mapPricedProductToAnalyticsItem(product)

  sendGoogleAnalyticsEvent("VIEW_ITEM", {
    items: [items],
  })

  sendPersonalizeAnalyticsEvent("View", product.id!, getImpressions())
}

const mapPricedProductToAnalyticsItem = (product: PricedProduct) => {
  const variant = product.variants?.[0]
  const calculatedPrice = variant?.calculated_price || 0
  const originalPrice = variant?.original_price || calculatedPrice
  const discountAmount = originalPrice - calculatedPrice

  return {
    item_id: product.id,
    item_name: product.title,
    discountt: (discountAmount / 100).toFixed(2),
    price: calculatedPrice / 100,
    totalPrice: originalPrice / 100,
    in_stock: product.variants?.some((v) => (v.inventory_quantity || 0) > 0 || v.allow_backorder),
  }
}

const mapSearchProductToAnalyticsItem = (product: SearchProduct, index?: number) => {
  const { discountAmount: discountAmountCents } = getPriceAndDiscounts(product)
  const discountAmount = discountAmountCents / 100

  return {
    item_id: product.productGroupUuid,
    item_name: product.name,
    discountt: discountAmount.toFixed(2),
    discounts: product.discounts?.map((d) => d.code).join("|"),
    price: product.price - discountAmount,
    totalPrice: product.price,
    in_stock: product.inStock,
    index: index,
  }
}

const appendImpressions = (products: SearchProduct[]) => {
  const productIds = products.map((product) => product.productGroupUuid)
  const impressions = JSON.parse(sessionStorage.getItem("product_impressions") || "[]") as Array<string>
  const computedImpressions = [...impressions, ...productIds].slice(-25)
  sessionStorage.setItem("product_impressions", JSON.stringify(computedImpressions))
}

const getImpressions = () => {
  return JSON.parse(sessionStorage.getItem("product_impressions") || "[]") as Array<string>
}
